import React, { useEffect, useMemo, useState } from 'react';
import { Trash2, Plus, Upload, AlertTriangle, CheckCircle2, Lock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import SocialFacePicker from './SocialFacePicker';

const eur = (n) => `€ ${(Number(n) || 0).toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Minuten zwischen zwei HH:MM (Check-out vor Check-in = Folgetag, wie im Backend). */
const minutesBetween = (a, b) => {
    const p = (t) => { const m = /^(\d{1,2}):(\d{2})$/.exec(t || ''); return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null; };
    const s = p(a), e = p(b);
    if (s === null || e === null || s === e) return 0;
    let d = e - s;
    if (d < 0) d += 24 * 60;
    return d;
};
const amountFor = (minutes, rate) => Math.round((minutes / 60) * (Number(rate) || 0) * 100 + Number.EPSILON) / 100;
const ROLES = [['model', 'Model'], ['location', 'Location / Wohnung'], ['sonstiges', 'Sonstiges']];
const isFlat = (p) => p.billing_type === 'flat';
// Betrag je Person: Pauschale oder Minuten/60 x Satz
const participantAmount = (p) => isFlat(p) ? (Math.round((Number(p.flat_amount) || 0) * 100) / 100) : amountFor(minutesBetween(p.check_in_time, p.check_out_time), p.hourly_rate);

/**
 * "Shooting abschließen": tatsaechliche Zeiten, Teilnehmer mit eigenen Zeiten und Saetzen, Team, Notiz.
 * Absenden = verbindlich bestaetigt UND zur Abrechnung freigegeben. Erzeugt die Shooting-Doku automatisch.
 */
const ShootingCompletionDialog = ({ open, shootingId, onOpenChange, onCompleted }) => {
    const { api } = useAuth();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [users, setUsers] = useState([]);
    const [data, setData] = useState(null);
    const [form, setForm] = useState(null);

    useEffect(() => {
        if (!open || !shootingId) return;
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const [res, usersRes] = await Promise.all([
                    api.get(`/sf/shootings/${shootingId}/completion`),
                    api.get('/users').catch(() => ({ data: [] })),
                ]);
                if (cancelled) return;
                setData(res.data);
                setUsers((usersRes.data || []).filter(u => u.is_active !== false && u.role !== 'influencer'));
                setForm({
                    shooting_date: res.data.shooting_date || '',
                    actual_start: res.data.actual_start || '',
                    actual_end: res.data.actual_end || '',
                    participants: (res.data.participants || []).map(p => ({ ...p, hourly_rate: p.hourly_rate ?? res.data.default_hourly_rate ?? 15 })),
                    team_members: (res.data.team_members || []).length ? res.data.team_members : [],
                    notes: res.data.notes || '',
                    note_images: res.data.note_images || [],
                });
            } catch (e) {
                toast.error(e.response?.data?.detail || 'Shooting konnte nicht geladen werden');
                onOpenChange(false);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, shootingId, api]);  // eslint-disable-line react-hooks/exhaustive-deps

    const totals = useMemo(() => {
        if (!form) return { minutes: 0, amount: 0 };
        let minutes = 0, amount = 0;
        form.participants.forEach(p => {
            minutes += minutesBetween(p.check_in_time, p.check_out_time);
            amount += participantAmount(p);
        });
        return { minutes, amount: Math.round(amount * 100) / 100 };
    }, [form]);

    const setP = (idx, field, value) => setForm(f => ({ ...f, participants: f.participants.map((p, i) => i === idx ? { ...p, [field]: value } : p) }));
    const removeP = (idx) => setForm(f => ({ ...f, participants: f.participants.filter((_, i) => i !== idx) }));
    const addFace = (face) => setForm(f => f.participants.some(p => p.social_face_id === face.id) ? f : ({
        ...f,
        participants: [...f.participants, {
            social_face_id: face.id, name: face.name, first_name: face.first_name, last_name: face.last_name,
            check_in_time: f.actual_start || '', check_out_time: f.actual_end || '',
            hourly_rate: data?.default_hourly_rate ?? 15, status: 'planned', locked: false,
            role: 'model', description: '', billing_type: 'hourly', flat_amount: '',
        }],
    }));
    const setT = (idx, patch) => setForm(f => ({ ...f, team_members: f.team_members.map((t, i) => i === idx ? { ...t, ...patch } : t) }));

    // Shooting-Zeiten auf alle noch nicht bearbeiteten Personen uebertragen
    const applyShootingTimes = () => setForm(f => ({
        ...f, participants: f.participants.map(p => p.locked ? p : { ...p, check_in_time: f.actual_start, check_out_time: f.actual_end }),
    }));

    const uploadImage = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await api.post('/forms/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setForm(f => ({ ...f, note_images: [...f.note_images, res.data.url] }));
        } catch (err) {
            toast.error('Upload fehlgeschlagen');
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    const submit = async () => {
        if (!form) return;
        const missing = form.participants.filter(p => !isFlat(p) && (!p.check_in_time || !p.check_out_time));
        if (missing.length) { toast.error('Bitte bei jeder Person Check-in und Check-out eintragen (oder Pauschale wählen)'); return; }
        const noAmount = form.participants.filter(p => isFlat(p) && !(Number(p.flat_amount) > 0));
        if (noAmount.length) { toast.error('Bitte bei jeder Pauschale einen Betrag eintragen'); return; }
        if (form.participants.length === 0 && !window.confirm('Ohne Teilnehmer wird nichts abgerechnet. Trotzdem abschließen?')) return;
        if (!window.confirm(`Shooting verbindlich abschließen?\n\n${form.participants.length} Personen, ${Math.round(totals.minutes)} Minuten, ${eur(totals.amount)}.\nDamit ist es zur Abrechnung freigegeben.`)) return;
        setSaving(true);
        try {
            const res = await api.post(`/sf/shootings/${shootingId}/complete`, {
                shooting_date: form.shooting_date,
                actual_start: form.actual_start || null,
                actual_end: form.actual_end || null,
                participants: form.participants.map(p => ({
                    social_face_id: p.social_face_id, check_in_time: p.check_in_time || '', check_out_time: p.check_out_time || '',
                    hourly_rate: Number(p.hourly_rate) || null,
                    role: p.role || 'model', description: p.description || '',
                    billing_type: isFlat(p) ? 'flat' : 'hourly', flat_amount: isFlat(p) ? Number(p.flat_amount) : null,
                })),
                team_members: form.team_members.filter(t => t.user_id || t.first_name).map(t => ({
                    user_id: t.user_id || null, first_name: t.first_name || '', last_name: t.last_name || '', position: t.position || '',
                })),
                notes: form.notes || '',
                note_images: form.note_images,
            });
            const r = res.data;
            toast.success(`Abgeschlossen und freigegeben: ${r.participants.length} Personen, ${eur(r.total_amount)}${r.needs_admin_review ? ' · zur Admin-Prüfung markiert' : ''}`, { duration: 8000 });
            onOpenChange(false);
            if (onCompleted) onCompleted(r);
        } catch (e) {
            const d = e.response?.data?.detail;
            if (d && typeof d === 'object') toast.error([d.message, ...(d.errors || [])].filter(Boolean).join(' · '), { duration: 10000 });
            else toast.error(d || 'Abschluss fehlgeschlagen');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[96vw] max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl p-4 sm:p-6" data-testid="shooting-completion-dialog">
                <DialogHeader>
                    <DialogTitle>Shooting abschließen{data?.title ? ` · ${data.title}` : ''}</DialogTitle>
                    <DialogDescription>
                        {data?.customer_name ? `${data.customer_name} · ` : ''}Trage die echten Zeiten ein. Absenden ist verbindlich und gibt das Shooting zur Abrechnung frei.
                    </DialogDescription>
                </DialogHeader>

                {loading || !form ? (
                    <div className="py-12 flex justify-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
                ) : (
                    <div className="space-y-5">
                        {data?.is_invoiced && (
                            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm flex gap-2">
                                <Lock className="w-4 h-4 shrink-0 mt-0.5" />
                                Für dieses Shooting gibt es bereits eine Rechnung. Abgerechnete Personen sind gesperrt, Änderungen kann nur ein Admin machen.
                            </div>
                        )}
                        {data?.completed_at && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> Bereits abgeschlossen von {data.completed_by_name || 'unbekannt'} am {new Date(data.completed_at).toLocaleString('de-AT')}. Du bearbeitest den Abschluss.
                            </p>
                        )}

                        {/* Tatsaechliche Shooting-Zeiten */}
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <Label className="text-xs text-muted-foreground">Datum</Label>
                                <Input type="date" value={form.shooting_date} onChange={(e) => setForm(f => ({ ...f, shooting_date: e.target.value }))} className="h-10" />
                            </div>
                            <div>
                                <Label className="text-xs text-muted-foreground">Tatsächlich von</Label>
                                <Input type="time" value={form.actual_start} onChange={(e) => setForm(f => ({ ...f, actual_start: e.target.value }))} className="h-10" data-testid="completion-actual-start" />
                            </div>
                            <div>
                                <Label className="text-xs text-muted-foreground">Tatsächlich bis</Label>
                                <Input type="time" value={form.actual_end} onChange={(e) => setForm(f => ({ ...f, actual_end: e.target.value }))} className="h-10" data-testid="completion-actual-end" />
                            </div>
                        </div>
                        <button type="button" onClick={applyShootingTimes} className="text-xs text-primary hover:underline">
                            Diese Zeiten für alle Personen übernehmen
                        </button>

                        {/* Teilnehmer */}
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Personen · Check-in, Check-out, Stundensatz je Person</Label>
                            <p className="text-xs text-muted-foreground">Auch wer nicht als Model dabei war, gehört hier rein – z. B. wer die Wohnung vermietet hat: Rolle wählen, Beschreibung eintragen, bei Bedarf Pauschale statt Stunden.</p>
                            {form.participants.length === 0 && (
                                <p className="text-sm text-muted-foreground">Niemand eingetragen. Wer nicht da war, wird nicht abgerechnet.</p>
                            )}
                            {form.participants.map((p, idx) => {
                                const m = minutesBetween(p.check_in_time, p.check_out_time);
                                const flat = isFlat(p);
                                return (
                                    <div key={p.social_face_id} className={`rounded-xl border p-3 ${p.locked ? 'opacity-70 bg-muted/40' : 'bg-muted/20'}`} data-testid={`completion-participant-${p.social_face_id}`}>
                                        <div className="flex items-center justify-between gap-2 mb-2">
                                            <span className="font-medium text-sm truncate">{p.name || `${p.first_name} ${p.last_name}`}</span>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <span className="text-xs text-muted-foreground">{flat ? 'pauschal' : `${m} min`}</span>
                                                <span className="text-sm font-semibold">{eur(participantAmount(p))}</span>
                                                {p.locked ? <Lock className="w-4 h-4 text-muted-foreground" /> : (
                                                    <button type="button" onClick={() => removeP(idx)} className="p-1 hover:bg-destructive/20 rounded" title="War nicht da">
                                                        <Trash2 className="w-4 h-4 text-destructive" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-3 gap-2">
                                            <div>
                                                <Label className="text-[10px] text-muted-foreground">Rolle</Label>
                                                <select value={p.role || 'model'} disabled={p.locked} onChange={(e) => setP(idx, 'role', e.target.value)}
                                                    className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
                                                    {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                                </select>
                                            </div>
                                            <div className="col-span-2">
                                                <Label className="text-[10px] text-muted-foreground">Beschreibung (steht auf der Honorarnote)</Label>
                                                <Input value={p.description || ''} disabled={p.locked} placeholder={p.role === 'location' ? 'z. B. Wohnung für das Shooting vermietet' : 'optional'}
                                                    onChange={(e) => setP(idx, 'description', e.target.value)} className="h-9" maxLength={200} />
                                            </div>
                                            <div>
                                                <Label className="text-[10px] text-muted-foreground">Abrechnung</Label>
                                                <select value={flat ? 'flat' : 'hourly'} disabled={p.locked} onChange={(e) => setP(idx, 'billing_type', e.target.value)}
                                                    className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
                                                    <option value="hourly">nach Stunden</option>
                                                    <option value="flat">Pauschale</option>
                                                </select>
                                            </div>
                                            <div>
                                                <Label className="text-[10px] text-muted-foreground">Check-in{flat ? ' (optional)' : ''}</Label>
                                                <Input type="time" value={p.check_in_time || ''} disabled={p.locked} onChange={(e) => setP(idx, 'check_in_time', e.target.value)} className="h-9" />
                                            </div>
                                            <div>
                                                <Label className="text-[10px] text-muted-foreground">Check-out{flat ? ' (optional)' : ''}</Label>
                                                <Input type="time" value={p.check_out_time || ''} disabled={p.locked} onChange={(e) => setP(idx, 'check_out_time', e.target.value)} className="h-9" />
                                            </div>
                                            {flat ? (
                                                <div className="col-span-3">
                                                    <Label className="text-[10px] text-muted-foreground">Pauschalbetrag in €</Label>
                                                    <Input type="number" step="0.01" min="0" value={p.flat_amount ?? ''} disabled={p.locked} onChange={(e) => setP(idx, 'flat_amount', e.target.value)} className="h-9" />
                                                </div>
                                            ) : (
                                                <div className="col-span-3">
                                                    <Label className="text-[10px] text-muted-foreground">€ / Stunde</Label>
                                                    <Input type="number" step="0.5" min="0" value={p.hourly_rate} disabled={p.locked} onChange={(e) => setP(idx, 'hourly_rate', e.target.value)} className="h-9" />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            <div className="p-3 rounded-xl border border-dashed border-border">
                                <Label className="text-xs text-muted-foreground mb-1 block">Person hinzufügen (spontan dazugekommen?)</Label>
                                <SocialFacePicker allowCreate excludeIds={form.participants.map(p => p.social_face_id)} onSelect={addFace} inputClassName="h-9" />
                            </div>
                        </div>

                        {/* Summe */}
                        <div className="flex items-center justify-between rounded-xl bg-primary/10 border border-primary/30 px-4 py-3">
                            <span className="text-sm">Gesamt · {form.participants.length} Personen · {totals.minutes} Minuten</span>
                            <span className="text-lg font-bold">{eur(totals.amount)}</span>
                        </div>

                        {/* Team */}
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Team vor Ort</Label>
                            {form.team_members.map((t, idx) => (
                                <div key={idx} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                                    <Select value={t.user_id || '__extern__'} onValueChange={(v) => {
                                        if (v === '__extern__') setT(idx, { user_id: '', first_name: '', last_name: '' });
                                        else { const u = users.find(x => x.id === v); setT(idx, { user_id: v, first_name: u?.first_name || '', last_name: u?.last_name || '' }); }
                                    }}>
                                        <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Teammitglied" /></SelectTrigger>
                                        <SelectContent>
                                            {users.map(u => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
                                            <SelectItem value="__extern__">Extern</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <Input placeholder="Position" value={t.position || ''} onChange={(e) => setT(idx, { position: e.target.value })} className="h-9 text-sm" />
                                    <button type="button" onClick={() => setForm(f => ({ ...f, team_members: f.team_members.filter((_, i) => i !== idx) }))} className="p-1 hover:bg-destructive/20 rounded">
                                        <Trash2 className="w-4 h-4 text-destructive" />
                                    </button>
                                    {!t.user_id && (
                                        <div className="col-span-3 grid grid-cols-2 gap-2">
                                            <Input placeholder="Vorname" value={t.first_name || ''} onChange={(e) => setT(idx, { first_name: e.target.value })} className="h-9 text-sm" />
                                            <Input placeholder="Nachname" value={t.last_name || ''} onChange={(e) => setT(idx, { last_name: e.target.value })} className="h-9 text-sm" />
                                        </div>
                                    )}
                                </div>
                            ))}
                            <Button type="button" variant="outline" size="sm" onClick={() => setForm(f => ({ ...f, team_members: [...f.team_members, { user_id: '', first_name: '', last_name: '', position: '' }] }))}>
                                <Plus className="w-4 h-4 mr-1" /> Person
                            </Button>
                        </div>

                        {/* Notiz + Bilder */}
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Notiz</Label>
                            <Textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Verspätungen, Equipment, Besonderheiten..." className="min-h-[80px]" />
                            <div className="flex flex-wrap gap-2 items-center">
                                {form.note_images.map((url, i) => (
                                    <div key={url} className="relative">
                                        <img src={url} alt="" className="w-16 h-16 object-cover rounded-lg border" />
                                        <button type="button" onClick={() => setForm(f => ({ ...f, note_images: f.note_images.filter((_, j) => j !== i) }))} className="absolute -top-1 -right-1 bg-background border rounded-full p-0.5">
                                            <Trash2 className="w-3 h-3 text-destructive" />
                                        </button>
                                    </div>
                                ))}
                                <label className="inline-flex items-center gap-1 text-xs text-primary cursor-pointer">
                                    <Upload className="w-3.5 h-3.5" /> {uploading ? 'Lädt...' : 'Bild anhängen'}
                                    <input type="file" accept="image/*" className="hidden" onChange={uploadImage} disabled={uploading} />
                                </label>
                            </div>
                        </div>

                        {data?.needs_admin_review && (
                            <p className="text-xs text-amber-500 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Zur Admin-Prüfung markiert (Shootingleitung ist selbst Teilnehmer).</p>
                        )}
                    </div>
                )}

                <DialogFooter className="mt-2">
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>Abbrechen</Button>
                    <Button onClick={submit} disabled={saving || loading || !form} data-testid="completion-submit">
                        {saving ? 'Wird gespeichert...' : data?.completed_at ? 'Abschluss aktualisieren' : 'Verbindlich abschließen'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ShootingCompletionDialog;
